import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { errorMessage, useCompleteHubSpotInstall } from '../../../../api/integrations.js';
import { LoadingArea, StartPageLink, TextPage } from '../../../../components/PlaceholderPage.js';

/**
 * `/integrations/hubspot/callback`: where HubSpot sends the browser back after
 * "Connect HubSpot" (`features/integrations`). It hands HubSpot's code and the
 * state to the api, which connects the account, then opens the website's
 * Integrations. Turning HubSpot down comes back with `error` instead.
 */
const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute('/_app/integrations/hubspot/callback')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: HubSpotCallbackPage,
});

const TITLE = "HubSpot wasn't connected";

function HubSpotCallbackPage() {
  const { code, state, error } = Route.useSearch();
  const navigate = useNavigate();
  const complete = useCompleteHubSpotInstall();
  const sent = useRef(false);

  const canComplete = error === undefined && code !== undefined && state !== undefined;

  useEffect(() => {
    if (!canComplete || sent.current) return;
    sent.current = true;
    complete.mutate(
      { code, state },
      {
        onSuccess: ({ websiteId }) =>
          void navigate({
            to: '/websites/$websiteId/integrations',
            params: { websiteId },
            search: { integration: 'hubspot' },
            replace: true,
          }),
      },
    );
  }, [canComplete, code, state, complete, navigate]);

  if (error !== undefined) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {error === 'access_denied'
          ? "Groundly wasn't allowed to access HubSpot. Connect HubSpot again from the website's Integrations when you're ready."
          : "HubSpot stopped before the connection was made. Connect HubSpot again from the website's Integrations."}
      </TextPage>
    );
  }
  if (!canComplete) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        This page is only for coming back from HubSpot. Connect HubSpot from the website&apos;s
        Integrations.
      </TextPage>
    );
  }
  if (complete.isError) {
    return (
      <TextPage title={TITLE} action={<StartPageLink />}>
        {errorMessage(complete.error, "Groundly couldn't reach the server. Connect HubSpot again.")}
      </TextPage>
    );
  }
  return <LoadingArea />;
}
